import { isAbsoluteUrl } from '../classes/utilities';
import { HttpService } from './http-service';

const fontFilesLoading = new Map<string, Promise<ArrayBuffer>>();
const stylesheetsLoading = new Map<string, Promise<void>>();
const fontFamilySrcs = new Map<string, string>();

export class FontService {
    constructor(private httpService: HttpService, private assetRoot = '') {}

    /** Can be both the font face name as well as the font file URL */
    public readyFonts = new Map<string, ArrayBuffer>();

    loadFont(stylesheetUrl: string, fontFamilies: string[]): Promise<FontFace[]> {
        const fontFaceSet: FontFaceSet = (document as any).fonts;
        if (typeof fontFaceSet === 'undefined') {
            return Promise.reject('FontFaceSet api not available');
        }

        return this.loadStylesheet(stylesheetUrl).then(() => this.loadFontFamily(fontFaceSet, fontFamilies));
    }

    loadFontByFile(path: string) {
        const absPath = isAbsoluteUrl(path) ? path : this.getAbsoluteFontPath(path);
        let loadPromise: Promise<ArrayBuffer>;
        if (fontFilesLoading.has(absPath)) {
            loadPromise = fontFilesLoading.get(absPath)!;
        } else {
            const promise = this.httpService.getPublicStatic(absPath, 'arraybuffer');
            fontFilesLoading.set(absPath, promise);
            loadPromise = promise;
        }

        return loadPromise.then(buffer => {
            if (buffer.byteLength === 0) {
                return Promise.reject('Could not fetch font');
            } else {
                this.readyFonts.set(path, buffer);
            }
            return buffer;
        });
    }

    loadFontData(stylesheetUrl: string, fontFamilies: string[]) {
        return this.loadStylesheet(stylesheetUrl).then(() => this.getFontData(fontFamilies));
    }

    getAbsoluteFontPath(relativePath: string) {
        return `${this.assetRoot}/media/${relativePath}`; //it's a relative path to the media folder
    }

    private loadStylesheet(stylesheetUrl: string) {
        let stylesheetLoad = stylesheetsLoading.get(stylesheetUrl);
        if (!stylesheetLoad) {
            stylesheetLoad = this.httpService.getPublicStatic(stylesheetUrl).then(res => {
                // Parse the css and store the srcs to the font files
                const fontFaces = res.matchAll(/@font-face\s*{([\s\S]*?)}/gm);
                for (const face of fontFaces) {
                    const fontFamilyMatch = face[1].match(/font-family\s*:\s*['"]?(.*?)['"]?[;,\n]/m);

                    if (fontFamilyMatch) {
                        const fontFamilyName = fontFamilyMatch[1];
                        const srcMatch = face[1].match(/src\s*:.*?url\s*\([\s"']*(.*?)[\s"']*\)/m);
                        if (srcMatch) {
                            const fontSrc = srcMatch[1];
                            fontFamilySrcs.set(fontFamilyName.trim().toLowerCase(), fontSrc.trim());
                        }
                    }
                }
            });
            stylesheetsLoading.set(stylesheetUrl, stylesheetLoad);
        }

        return stylesheetLoad;
    }

    private getFontData(fontFamilies: string[]) {
        const loadFaces: string[] = [];
        const loadPromises: Array<Promise<ArrayBuffer>> = [];

        fontFamilies.forEach(fontFamily => {
            const fontSrc = fontFamilySrcs.get(fontFamily.trim().toLowerCase());
            if (!fontSrc) {
                console.warn(`Font family ${fontFamily} not found. Is it being loaded by the preloaded stylesheet?`);
            } else if (fontFilesLoading.has(fontFamily)) {
                loadPromises.push(fontFilesLoading.get(fontFamily)!);
                loadFaces.push(fontFamily);
            } else {
                const promise = this.httpService.getPublicStatic(fontSrc, 'arraybuffer');
                fontFilesLoading.set(fontFamily, promise);
                loadPromises.push(promise);
                loadFaces.push(fontFamily);
            }
        });

        const resMap: Record<string, ArrayBuffer> = {};
        return Promise.all(loadPromises).then(buffers => {
            buffers.forEach((buffer, index) => {
                resMap[loadFaces[index]] = buffer;
                this.readyFonts.set(loadFaces[index], buffer);
            });
            return resMap;
        });
    }

    private loadFontFamily(fontFaceSet: FontFaceSet, fontFamilies: string[]): Promise<FontFace[]> {
        return this.getFontData(fontFamilies).then(resultData => {
            const resultFonts: FontFace[] = [];
            fontFamilies.forEach(fontFamily => {
                if (resultData[fontFamily]) {
                    const newFontFace = new FontFace(fontFamily, resultData[fontFamily]);
                    // @ts-expect-error TS 5.1.6 definition does not include add on FontFaceSet
                    fontFaceSet.add(newFontFace);
                    resultFonts.push(newFontFace);
                } else {
                    console.warn('Could not load font ' + fontFamily);
                }
            });
            return resultFonts;
        });
    }
}
